import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress, 
    Button,
    Stack,
} from '@mui/material';
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'state/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import ScrollToTop from 'components/ScrollToTop';
import CustomDataGridToolbar from 'components/CustomDataGridToolbar';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import InvoiceDialog from 'scenes/inflightSales/InvoiceDialog';
import CrewSalesCard from 'scenes/inflightSales/CrewSalesCard';


const CrewSales = () => {
    useAuthCheck();
    const user = useSelector((state) => state.auth.user);
    const crewCode = user.crewCode;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);    

    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });
    
    const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
    const [selectedCrewCode, setSelectedCrewCode] = useState('');
    const [selectedCrewName, setSelectedCrewName] = useState('');
    const [selectedFlights, setSelectedFlights] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // const [invoicedFlights, setInvoicedFlights] = useState([]);
    const [invoices, setInvoices] = useState([]);
    
    const handleInvoices = useCallback(async () => {
        if (!startDate || !endDate) return;

        try {
            setError(null);
            setLoading(true);
            const res = await api.post('/inflightsales/crewmembersalesbycrewcode', { crewCode, startDate, endDate });
            setInvoices(res.data);
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [crewCode, startDate, endDate]);

    useEffect(() => {
        handleInvoices();      
    }, [handleInvoices]);

    const handleViewClick = async () => {        
        setOpenInvoiceDialog(true);
    };

    const handleInvoiceSaved = (flights) => {
        // setInvoicedFlights(prev => [...prev, ...flights]);
        setOpenInvoiceDialog(false); 
        handleInvoices();
    };
    
    const handleCloseInvoiceDialog = () => {
        setSelectedInvoiceId(null);
        setSelectedCrewCode(null);
        setSelectedCrewName(null);
        setSelectedFlights([]);  
        setCrewMembers([]);
        setSelectedProducts([]);
        setOpenInvoiceDialog(false); 
    };

    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            headerClassName: 'theme-column--header',
            width: 30,
            filterable: false,      
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
        },
        {
            field: "crewCode",
            headerName: "Code",
            headerClassName: 'theme-column--header',
            width: 60,
        },
        {
            field: "crewName",
            headerName: "InputBy",
            headerClassName: 'theme-column--header',
            width: 280,
        },
        {
            field: "createdAt",
            headerName: "CreatedAt",
            headerClassName: 'theme-column--header',
            width: 130, 
            pinned: 'left',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "HH:mm:ss dd/MM/yy")}`;
            },
        },
        {
            field: "flightDate",
            headerName: "RouteDate",
            headerClassName: 'theme-column--header',
            width: 100, 
            pinned: 'left',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM/yy")}`;
            },
        },        
        {
            field: "flightNo",
            headerName: "FlightNo",
            width: 200,
            pinned: 'left',
            headerClassName: 'theme-column--header',
            cellClassName: "name-column--cell",
        },        
        {
            field: "crewRoute",
            headerName: "CrewRoute",
            headerClassName: 'theme-column--header',
            width: 200,
        },
        {
            field: 'selectedFlights',
            headerName: 'FlightList',
            width: isMobile ? 265 : 280,
            renderCell: (params) => {                
                const sortedFlights = [...(params.row.selectedFlights || [])].sort((a, b) => {
                    const dateComparison = new Date(a.flightDate) - new Date(b.flightDate);
                    if (dateComparison !== 0) return dateComparison; 
                    return a.etd.localeCompare(b.etd); 
                });

                return (
                    <Box
                        sx={{
                            height: '100%',
                            alignItems: 'flex-start',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                    >
                        {sortedFlights.map((flight, index) => (
                            <Box
                                key={index}
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'right',
                                        whiteSpace: 'nowrap',
                                        width: 32,
                                    }}
                                >
                                    {flight.flightNo}
                                </Typography>
                                
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'center',
                                        width: 45,
                                    }}
                                >
                                    {format(flight.flightDate, "ddMMMyy")}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'center',
                                        width: 30,
                                    }}
                                >
                                    {flight.etd}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'center',
                                        width: 25,
                                    }}
                                >
                                    {flight.dep}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'center',
                                        width: 25,
                                    }}
                                >
                                    {flight.arr}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'center',
                                        width: 30,
                                    }}
                                >
                                    {flight.eta}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                );
            },
        },
        {
            field: 'crewMembers',
            headerName: 'CrewMembers',
            width: isMobile ? 330 : 350,
            renderCell: (params) => (
                <Box
                    sx={{
                        height: '100%',                        
                        alignItems: 'flex-start', 
                        overflowY: 'auto', 
                        overflowX: 'hidden',
                    }}
                >
                    {params.row.crewMembers?.map((member, index) => (
                        <Box
                            key={index}
                            display="flex"
                            alignItems="center"
                            gap={1}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                    width: 30,
                                }}
                            >
                                {member.crewCode}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'left',
                                }}
                            >
                                {member.crewName}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'center',
                                }}
                            >
                                ({member.post} | {member.base})
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ),
        },
        {
            field: 'products',
            headerName: 'Products',
            width: isMobile ? 320 : 350,
            renderCell: (params) => {
                const filteredProducts = params.row.products
                    ?.filter(product => product.preorderQuantity > 0 || product.directQuantity > 0);
                    
                return (
                    <Box
                        sx={{
                            height: '100%',
                            alignItems: 'flex-start',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        }}
                    >
                        {filteredProducts?.map((product, index) => {
                            const totalQuantity = (product.preorderQuantity || 0) + (product.directQuantity || 0);                            
                            return (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap',
                                            width: 12,
                                        }}
                                    >
                                        {`${index + 1}. `}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'left',
                                            whiteSpace: 'nowrap',
                                            width: isMobile ? 220 : 250,
                                        }}
                                    >
                                        {product.productName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap',
                                            width: 20,
                                        }}
                                    >
                                        {totalQuantity}
                                    </Typography>
                                </Box>
                            )}
                        )}
                    </Box>
                );
            },
        },
        {
            field: "preorderAmount",
            headerName: "PreorderAmount",
            headerClassName: 'theme-column--header',
            width: 150,
            // headerAlign: 'right', 
            align: 'right',
            valueFormatter: (params) => {
                return params ? params.toLocaleString() : '';
            },
        },
        {
            field: "directAmount",
            headerName: "DirectAmount",
            headerClassName: 'name-column--cell',
            cellClassName: "name-column--cell",
            width: 150,
            align: 'right',
            valueFormatter: (params) => {
                return params ? params.toLocaleString() : '';
            },
        },
        {
            field: "totalAmount",
            headerName: "TotalAmount",
            headerClassName: 'theme-column--header',
            width: 150,
            align: 'right',
            valueFormatter: (params) => {
                return params ? params.toLocaleString() : '';
            },
        },
        {
            field: "preorderCommission",
            headerName: "PreorderCommission",
            headerClassName: 'theme-column--header',
            width: 150,
            align: 'right',
            valueFormatter: (params) => {
                return params ? params.toLocaleString() : '';
            },
        },        
        {
            field: "directCommission",
            headerName: "DirectCommission",
            headerClassName: 'name-column--cell',
            cellClassName: "name-column--cell",
            width: 150,
            align: 'right',
            valueFormatter: (params) => {
                return params ? params.toLocaleString() : '';
            },
        },
        {
            field: "totalCommission",
            headerName: "TotalCommission",
            headerClassName: 'theme-column--header',
            width: 150,
            align: 'right',
            valueFormatter: (params) => {
                return params ? params.toLocaleString() : '';
            },
        },
        {
            field: "payableAmount",
            headerName: "Payable",
            headerClassName: 'name-column--cell',
            cellClassName: "name-column--cell",
            width: 150,
            // headerAlign: 'right', 
            align: 'right',
            valueFormatter: (params) => {
                return params ? params.toLocaleString() : '';
            },
        }, 
        {
            field: "crewCount",
            headerName: "CrewCount",
            headerClassName: 'theme-column--header',
            width: 120,
            align: 'right',
            // valueFormatter: (params) => {
            //     return params ? params.toLocaleString() : '';
            // },
        }, 
        {
            field: "eachCrewTotalCommission",
            headerName: "OwnCommission",
            headerClassName: 'theme-column--header',
            width: 150,
            align: 'right',
            valueFormatter: (params) => {
                return params ? params.toLocaleString() : '';
            },
        }, 
    ], [isMobile]);
    
    const hiddenColumns = useMemo(() => {
        return {
            _id: false,
            no: false,   
            // flightNo: false,
            // crewRoute: false,
            // preorderAmount: false,
            // directAmount: false,
            totalAmount: false,
            preorderCommission: false,  
            // directCommission: false,  
            totalCommission: false,  
            // payableAmount: false,  
        }
    }, []);

    const nonMobileHiddenColumns = useMemo(() => {
        return {
            _id: false,       
            totalAmount: false,
            totalCommission: false,    
        }  
    }, []);
    
    const actionColumn = useMemo(() => [{
        field: "action",
        headerName: "Action",
        width: 80,
        filterable: false,      
        sortable: false,
        renderCell: (params) => (
          <Box >
            <Button
                aria-label="View"
                style={{ color: theme.palette.primary.dark }}
                onClick={() => {
                    setSelectedInvoiceId(params.row._id);
                    setSelectedCrewCode(params.row.crewCode);
                    setSelectedCrewName(params.row.crewName);
                    setSelectedFlights(params.row.selectedFlights);
                    setCrewMembers(params.row.crewMembers);
                    setSelectedProducts(params.row.products);
                    
                    if(user.crewCode === params.row.crewCode) {
                        handleViewClick(params.row._id)
                    } else {
                        toast.error("Not allowed to open invoice of other crew.");
                    }
                }}
            >
              <VisibilityIcon />
            </Button>
          </Box>
        ),
    }], [invoices]); // eslint-disable-line react-hooks/exhaustive-deps
    
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        isMobile ? hiddenColumns : nonMobileHiddenColumns
    );

    useEffect(() => {
        setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
    }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);
    
    if (loading) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
        </FlexCenter>
    );

    if (error) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">{error?.message}</Typography>
        </FlexCenter>
    );
    

    return (
        <Box className="main-container-box">          
            <FlexBetween>
                <Header 
                    title="Crew Sales" 
                    subtitle={`${loading ? 'Loading...' : 'List of Sales'}`}
                /> 
            </FlexBetween>        
            
            <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
            >
                <Box
                    ref={scrollRef}
                    height={LAYOUT_HEIGHT}  
                    sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
                >      
                    {isMobile ? (
                        <Box sx={{ overflowY: 'auto', height: LAYOUT_HEIGHT, pr: 0.5 }}>
                            <Stack spacing={1.5}>
                                {invoices.map((invoice, index) => (
                                    <CrewSalesCard
                                        key={invoice._id}
                                        index={index + 1}
                                        invoice={invoice}
                                        onView={() => {
                                            setSelectedInvoiceId(invoice._id);
                                            setSelectedCrewCode(invoice.crewCode);
                                            setSelectedCrewName(invoice.crewName);
                                            setSelectedFlights(invoice.selectedFlights);
                                            setCrewMembers(invoice.crewMembers);
                                            setSelectedProducts(invoice.products);
                                            if (user.crewCode === invoice.crewCode) {
                                                handleViewClick();
                                            } else {
                                                toast.error("Not allowed to open invoice of other crew.");
                                            }
                                        }}
                                    />
                                ))}
                                {invoices.length === 0 && (
                                    <FlexCenter height="200px">
                                        <Typography color="text.secondary">
                                            No sales found
                                        </Typography>
                                    </FlexCenter>
                                )}
                            </Stack>
                        </Box>
                    ) : (
                        <DataGrid
                            getRowId={(row) => row._id}
                            rows={(invoices) || []}
                            columns={[...columns, ...actionColumn]}
                            rowsPerPageOptions={[50, 100, 200]}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                            showToolbar
                            slots={{ toolbar: () => <CustomDataGridToolbar isExpanded /> }}
                            rowSelectionModel={rowSelectionModel}
                            onRowSelectionModelChange={(newRowSelectionModel) => {
                                setRowSelectionModel(newRowSelectionModel);
                            }}
                        />
                    )}

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            </Box>

            {openInvoiceDialog &&
                <InvoiceDialog
                    open={openInvoiceDialog}
                    onClose={handleCloseInvoiceDialog}
                    selectedInvoiceId={selectedInvoiceId}
                    crewCode={selectedCrewCode}
                    crewName={selectedCrewName}
                    selectedFlights={selectedFlights}
                    crewMembers={crewMembers}
                    selectedProducts={selectedProducts}
                    onInvoiceSaved={handleInvoiceSaved}
                />
            }
        </Box>
    );
};

export default CrewSales;
